import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

/**
 * Opciones del campo "Tipo de consulta" (spec 16.2). Son los valores del enum
 * `TipoConsulta` del backend, que los valida con `@IsEnum`: cualquier otro
 * string vuelve como 400.
 *
 * El portal ya no los ofrece (FP-48): el desplegable quedó sólo en el
 * formulario del backoffice. Acá sobrevive el tipo porque el backend sigue
 * exigiendo el campo — ver [[TIPO_CONSULTA_PORTAL]].
 */
export type TipoConsulta =
  | 'funcionamiento_general'
  | 'publicaciones_y_clientes'
  | 'operadores'
  | 'alianzas'
  | 'administracion_y_cuenta'
  | 'soporte'
  | 'consulta_web_publica';

/**
 * Tipo con el que se registran las consultas que entran por el portal (FP-48).
 *
 * `tipoConsulta` está marcado `@IsEnum` y sin valor por defecto en el DTO del
 * backend (`CrearConsultaContactoDto`), así que omitirlo devuelve 400. Como el
 * visitante ya no lo elige, se manda este valor fijo, que existe en el enum del
 * backend para este único uso: en la casilla de recepción la consulta llega
 * como "Consulta web pública", que es lo que la distingue de la que escribe un
 * broker desde el backoffice.
 */
const TIPO_CONSULTA_PORTAL: TipoConsulta = 'consulta_web_publica';

/** Tope de `mensaje` en el DTO del backend (`@MaxLength(400)`). */
export const MENSAJE_MAX = 400;

/**
 * Lo que el visitante completa en el formulario (spec 16.2). El `tipoConsulta`
 * que pide el DTO no está acá: lo agrega `enviar()`.
 */
export interface ConsultaContacto {
  nombre: string;
  email: string;
  mensaje: string;
}

/** Respuesta de `POST /contacto` (spec 16.3). */
export interface ConsultaContactoCreada {
  id: string;
  mensaje: string;
}

/**
 * Formulario de contacto del portal (FP-40).
 *
 * `POST /contacto` es público: el guard del endpoint es `JwtOpcionalGuard`, así
 * que no hace falta token (si viniera uno, el backend asocia la consulta al
 * usuario — algo que acá nunca pasa, el portal es anónimo). El backend además
 * descarta los reenvíos idénticos dentro de un minuto, así que un doble clic no
 * duplica la consulta.
 */
@Injectable({ providedIn: 'root' })
export class ContactoService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/contacto`;

  /** POST /contacto — registra la consulta y la reenvía a la casilla. */
  enviar(consulta: ConsultaContacto): Observable<ConsultaContactoCreada> {
    return this.http.post<ConsultaContactoCreada>(this.base, {
      ...consulta,
      tipoConsulta: TIPO_CONSULTA_PORTAL,
    });
  }
}
